/** Deterministic avatar background colours using design tokens. */
const USER_AVATAR_BACKGROUND_COLORS: ReadonlyArray<string> = [
  'var(--d-color-primary-500, #006e9d)',
  'var(--d-color-accent-600, #c2185b)',
  'var(--d-color-success-700, #00796b)',
  'var(--d-color-warning-700, #689f38)',
  'var(--d-color-orange-700, #f57c00)',
];

/**
 * Resolves the seed string used to pick a stable avatar colour.
 * Prefers email, then username.
 */
export function resolveUserAvatarSeed(email?: string | null, userName?: string | null): string {
  const emailSeed: string = email?.trim() ?? '';
  if (emailSeed) {
    return emailSeed.toLowerCase();
  }
  return (userName?.trim() ?? '').toLowerCase();
}

/** Returns a stable background colour for the given avatar seed. */
export function getUserAvatarBackgroundColor(seed: string): string {
  const hash: number = hashString(seed);
  return USER_AVATAR_BACKGROUND_COLORS[Math.abs(hash % USER_AVATAR_BACKGROUND_COLORS.length)];
}

/** Name fields used to derive avatar initials. */
export interface UserAvatarName {
  /** Preferred full display name. */
  displayName?: string | null;
  /** Fallback identifier, often an email or username. */
  userName?: string | null;
  /** Given / first name. Takes precedence over `displayName` when set. */
  givenName?: string | null;
  /** Family / last name. Takes precedence over `displayName` when set. */
  familyName?: string | null;
}

/**
 * Derives up to two initials from a person's name fields.
 * Given and family names win when either is set. Otherwise a multi-word display name uses the first and last word.
 * Returns an empty string when no name is available.
 *
 * @param name - Display name, username, and optional given and family names.
 */
export function getUserAvatarInitials(name: UserAvatarName): string {
  const given: string = name.givenName?.trim() ?? '';
  const family: string = name.familyName?.trim() ?? '';
  if (given || family) {
    const first: string = firstCodePoint(given);
    const last: string = firstCodePoint(family);
    return `${first}${last}`.toUpperCase();
  }

  const displayName: string = name.displayName?.trim() || name.userName?.trim() || '';
  if (!displayName) {
    return '';
  }

  const parts: Array<string> = displayName.split(/\s+/).filter((part: string): boolean => part.length > 0);
  if (parts.length >= 2) {
    const first: string = firstCodePoint(parts[0]);
    const last: string = firstCodePoint(parts[parts.length - 1]);
    return `${first}${last}`.toUpperCase();
  }

  return firstTwoCodePoints(parts[0]);
}

/** First Unicode code point of a string. */
function firstCodePoint(value: string): string {
  return Array.from(value)[0] ?? '';
}

/** Up to two code points from the start of a string, uppercased. */
function firstTwoCodePoints(value: string): string {
  return Array.from(value).slice(0, 2).join('').toUpperCase();
}

function hashString(value: string): number {
  let hash: number = 0;
  for (let i: number = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}
